import { useContext, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAssistantConfig, type AssistantConfig } from '@ohpen/enso-ui';

import { PmpConfigContext } from '@pmp/contexts';
import { useFeatureToggle } from '@pmp/hooks';
import { usePageContextStore } from '@pmp/stores';

import { clearPendingCrudClone } from './cloneRegistry';
import { clearPendingCrudDraft } from './crudRegistry';
import { registerAssistantAgentUrl } from './agentTransport';
import { toInAppPath } from './inAppLink';
import { clearPendingListFilter, registerListNavigator } from './listRegistry';
import { pmpAssistantConfig } from './pmpAssistant';

/**
 * Renders no DOM; must sit inside PmpConfigProvider to read the feature toggles and the agent URL.
 *
 * No permission check on top of the flag: the agent authorizes its tools fail-closed
 * per caller, so a user only ever gets the tools they may use. Passing null to
 * `useAssistantConfig` keeps the assistant runtime and its antd-X chunk unloaded.
 */
export const AssistantGate = (): null => {
  const flagEnabled = useFeatureToggle(['enableAssistant']);
  const agentUrl = useContext(PmpConfigContext)?.agentUrl ?? undefined;
  const { pathname } = useLocation();
  const clearOtherRoutes = usePageContextStore(state => state.clearOtherRoutes);
  const navigate = useNavigate();

  // The panel needs one stable config, so the current navigate is reached through a ref
  // rather than rebuilding the config whenever the router hands out a new one.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const config = useMemo<AssistantConfig>(
    () => ({
      ...pmpAssistantConfig,
      content: {
        ...pmpAssistantConfig.content,
        onLinkClick: href => {
          const path = toInAppPath(href, window.location.origin);
          if (path === null) return false;
          navigateRef.current(path);
          return true;
        },
      },
    }),
    [],
  );

  // Configuration has its own shell and no assistant of its own yet, so the launcher stays out
  // of its top bar. Passing null there also keeps the runtime and its antd-X chunk unloaded.
  const onConfigRoute = pathname.startsWith('/config');

  useAssistantConfig(flagEnabled && !onConfigRoute ? config : null);

  // The transport is a module-level function reached from outside the router, so its URL is
  // registered here rather than read at call time; re-registers whenever the BFF's resolved
  // value changes (an agent redeployed, or one that was missing now resolves).
  useEffect(() => {
    registerAssistantAgentUrl(agentUrl);
  }, [agentUrl]);

  // The agent filters the list the user is looking at, and opens it first when they are somewhere else, so
  // the navigating half needs a router. Registered here because this is the assistant's only component
  // inside the router; the transport that uses it is a module-level function.
  useEffect(() => {
    registerListNavigator(path => navigateRef.current(path));
    return () => {
      registerListNavigator(null);
      clearPendingListFilter();
      clearPendingCrudDraft();
      clearPendingCrudClone();
    };
  }, []);

  // Drops the page context of the route just left; scoped to "other routes" (not a blanket clear)
  // because the list on the route being entered has already reported itself by the time this runs.
  useEffect(() => clearOtherRoutes(pathname), [clearOtherRoutes, pathname]);

  return null;
};
